import * as THREE from 'three';
import type { BoardEngine } from '../board/engine';
import type { Coord, PieceKind, WorldTheme } from '../core/types';
import { WORLD_THEMES } from './palette';
import { createBlockerMesh, createPieceMesh, createRelicMesh } from './pieceFactory';
import { VfxSystem } from './vfx';

const CELL = 1.12;

interface PieceView {
  id: number;
  mesh: THREE.Group;
  r: number;
  c: number;
  bobPhase: number;
}

export class BoardView {
  readonly root = new THREE.Group();
  readonly boardRoot = new THREE.Group();
  readonly piecesRoot = new THREE.Group();
  readonly blockersRoot = new THREE.Group();
  readonly fx = new VfxSystem();
  private pieceViews = new Map<number, PieceView>();
  private cellMeshes: THREE.Mesh[][] = [];
  private underMeshes = new Map<string, THREE.Mesh>();
  private frame: THREE.Object3D | null = null;
  private engine: BoardEngine | null = null;
  private selected: Coord | null = null;
  private hover: Coord | null = null;
  private colorblind = false;
  private highContrast = false;
  private reducedMotion = false;
  private animating = false;
  private ambient: THREE.Group | null = null;
  private unsubBoard: (() => void) | null = null;

  constructor() {
    this.root.add(this.boardRoot);
    this.root.add(this.piecesRoot);
    this.root.add(this.blockersRoot);
    this.root.add(this.fx.root);
  }

  setAccessibility(opts: {
    colorblind?: boolean;
    highContrast?: boolean;
    reducedMotion?: boolean;
    reducedFlash?: boolean;
  }): void {
    this.colorblind = opts.colorblind ?? this.colorblind;
    this.highContrast = opts.highContrast ?? this.highContrast;
    this.reducedMotion = opts.reducedMotion ?? this.reducedMotion;
    this.fx.setAccessibility(
      opts.reducedMotion ?? false,
      opts.reducedFlash ?? false
    );
  }

  bind(engine: BoardEngine, theme: WorldTheme): void {
    if (this.unsubBoard) {
      this.unsubBoard();
      this.unsubBoard = null;
    }
    this.engine = engine;
    this.clear();
    this.buildBoard(engine, theme);
    this.syncAll();
    this.unsubBoard = engine.bus.on('board', (ev: unknown) => {
      const e = ev as { type: string; payload: Record<string, unknown> };
      this.onBoardEvent(e.type, e.payload);
    });
  }

  private clear(): void {
    for (const v of this.pieceViews.values()) {
      this.piecesRoot.remove(v.mesh);
    }
    this.pieceViews.clear();
    while (this.boardRoot.children.length) this.boardRoot.remove(this.boardRoot.children[0]!);
    while (this.blockersRoot.children.length)
      this.blockersRoot.remove(this.blockersRoot.children[0]!);
    this.underMeshes.clear();
    this.cellMeshes = [];
  }

  private buildBoard(engine: BoardEngine, theme: WorldTheme): void {
    const t = WORLD_THEMES[theme];
    const { rows, cols } = engine.grid;
    const ox = -((cols - 1) * CELL) / 2;
    const oy = ((rows - 1) * CELL) / 2;

    // Frame
    const frameGeo = new THREE.BoxGeometry(cols * CELL + 0.5, rows * CELL + 0.5, 0.25);
    const frameMat = new THREE.MeshStandardMaterial({
      color: t.frame,
      roughness: 0.4,
      metalness: 0.35,
      emissive: t.frame,
      emissiveIntensity: 0.08,
    });
    const frame = new THREE.Mesh(frameGeo, frameMat);
    frame.position.z = -0.2;
    this.boardRoot.add(frame);
    this.frame = frame;

    // Inner plate
    const plate = new THREE.Mesh(
      new THREE.BoxGeometry(cols * CELL + 0.15, rows * CELL + 0.15, 0.12),
      new THREE.MeshStandardMaterial({
        color: t.board,
        roughness: 0.6,
        metalness: 0.15,
      })
    );
    plate.position.z = -0.08;
    this.boardRoot.add(plate);

    // Cells
    for (let r = 0; r < rows; r++) {
      this.cellMeshes[r] = [];
      for (let c = 0; c < cols; c++) {
        const cell = engine.grid.get(r, c)!;
        if (!cell.active) {
          this.cellMeshes[r]![c] = null as unknown as THREE.Mesh;
          continue;
        }
        const mesh = new THREE.Mesh(
          new THREE.BoxGeometry(CELL * 0.92, CELL * 0.92, 0.08),
          new THREE.MeshStandardMaterial({
            color: (r + c) % 2 === 0 ? 0x1e293b : 0x243044,
            roughness: 0.7,
            metalness: 0.1,
            transparent: true,
            opacity: 0.85,
          })
        );
        mesh.position.set(ox + c * CELL, oy - r * CELL, 0);
        mesh.userData = { r, c };
        this.boardRoot.add(mesh);
        this.cellMeshes[r]![c] = mesh;

        if (cell.under) {
          const u = new THREE.Mesh(
            new THREE.PlaneGeometry(CELL * 0.85, CELL * 0.85),
            new THREE.MeshStandardMaterial({
              color: 0x67e8f9,
              emissive: 0x22d3ee,
              emissiveIntensity: 0.4,
              transparent: true,
              opacity: 0.45,
            })
          );
          u.position.set(ox + c * CELL, oy - r * CELL, 0.05);
          this.boardRoot.add(u);
          this.underMeshes.set(`${r},${c}`, u);
        }

        if (cell.portalTo) {
          const portal = new THREE.Mesh(
            new THREE.TorusGeometry(0.35, 0.05, 8, 20),
            new THREE.MeshStandardMaterial({
              color: 0xc084fc,
              emissive: 0xa855f7,
              emissiveIntensity: 0.6,
            })
          );
          portal.position.set(ox + c * CELL, oy - r * CELL, 0.1);
          this.boardRoot.add(portal);
        }

        if (cell.conveyorDir) {
          const arrow = new THREE.Mesh(
            new THREE.ConeGeometry(0.12, 0.25, 6),
            new THREE.MeshBasicMaterial({ color: 0x94a3b8, transparent: true, opacity: 0.5 })
          );
          arrow.position.set(ox + c * CELL, oy - r * CELL, 0.08);
          if (cell.conveyorDir === 'right') arrow.rotation.z = -Math.PI / 2;
          if (cell.conveyorDir === 'left') arrow.rotation.z = Math.PI / 2;
          if (cell.conveyorDir === 'up') arrow.rotation.z = 0;
          if (cell.conveyorDir === 'down') arrow.rotation.z = Math.PI;
          this.boardRoot.add(arrow);
        }
      }
    }

    // Center and scale board to fit camera for varying dimensions
    const scale = Math.min(1.15, 8.5 / Math.max(rows, cols));
    this.root.scale.setScalar(scale);
    this.root.position.set(0, 0.15, 0);
  }

  cellToWorld(r: number, c: number): THREE.Vector3 {
    if (!this.engine) return new THREE.Vector3();
    const { rows, cols } = this.engine.grid;
    const ox = -((cols - 1) * CELL) / 2;
    const oy = ((rows - 1) * CELL) / 2;
    return new THREE.Vector3(ox + c * CELL, oy - r * CELL, 0.25);
  }

  worldToCell(x: number, y: number): Coord | null {
    if (!this.engine) return null;
    const { rows, cols } = this.engine.grid;
    const ox = -((cols - 1) * CELL) / 2;
    const oy = ((rows - 1) * CELL) / 2;
    const c = Math.round((x - ox) / CELL);
    const r = Math.round((oy - y) / CELL);
    if (!this.engine.grid.inBounds(r, c) || !this.engine.grid.active(r, c)) return null;
    return { r, c };
  }

  syncAll(): void {
    if (!this.engine) return;
    const seen = new Set<number>();
    this.engine.grid.forEachActive((r, c, cell) => {
      if (cell.piece) {
        seen.add(cell.piece.id);
        let view = this.pieceViews.get(cell.piece.id);
        if (!view) {
          const mesh = createPieceMesh(cell.piece.kind, cell.piece.special, {
            colorblind: this.colorblind,
            highContrast: this.highContrast,
            beamAxis: cell.piece.beamAxis,
          });
          if (cell.piece.isRelic) mesh.add(createRelicMesh());
          const pos = this.cellToWorld(r, c);
          mesh.position.copy(pos);
          this.piecesRoot.add(mesh);
          view = {
            id: cell.piece.id,
            mesh,
            r,
            c,
            bobPhase: Math.random() * Math.PI * 2,
          };
          this.pieceViews.set(cell.piece.id, view);
        } else {
          view.r = r;
          view.c = c;
          const pos = this.cellToWorld(r, c);
          view.mesh.position.x = pos.x;
          view.mesh.position.y = pos.y;
        }
      }
      // blockers
    });
    // remove stale
    for (const [id, view] of this.pieceViews) {
      if (!seen.has(id)) {
        this.piecesRoot.remove(view.mesh);
        this.pieceViews.delete(id);
      }
    }
    this.syncBlockers();
  }

  private syncBlockers(): void {
    if (!this.engine) return;
    while (this.blockersRoot.children.length)
      this.blockersRoot.remove(this.blockersRoot.children[0]!);
    this.engine.grid.forEachActive((r, c, cell) => {
      if (cell.blocker) {
        const m = createBlockerMesh(cell.blocker.type, cell.blocker.layers);
        m.position.copy(this.cellToWorld(r, c));
        m.position.z = cell.blocker.type === 'crate' ? 0.35 : 0.2;
        this.blockersRoot.add(m);
      } else if (cell.sealed) {
        const m = createBlockerMesh('seal', 1);
        m.position.copy(this.cellToWorld(r, c));
        this.blockersRoot.add(m);
      }
    });
  }

  setSelected(coord: Coord | null): void {
    this.selected = coord;
  }

  setHover(coord: Coord | null): void {
    this.hover = coord;
  }

  private onBoardEvent(type: string, payload: Record<string, unknown>): void {
    if (!this.engine) return;
    switch (type) {
      case 'swap':
      case 'spawn':
      case 'fall':
      case 'clear':
      case 'special_created':
      case 'special_activated':
      case 'special_combo':
      case 'reshuffle':
      case 'blocker_damaged':
      case 'under_cleared':
        this.syncAll();
        break;
    }
    if (type === 'clear' || type === 'match') {
      const cells = (payload.cells ?? payload.groups) as unknown;
      // sparkle at center
      if (Array.isArray(cells) && cells[0] && typeof cells[0] === 'object' && 'r' in (cells[0] as object)) {
        for (const c of cells as Coord[]) {
          const p = this.cellToWorld(c.r, c.c);
          this.fx.burst(p, 0xffffff, 8, 1.8, 0.4);
        }
      }
    }
    if (type === 'special_activated' || type === 'special_combo') {
      const at = payload.at as Coord | undefined;
      if (at) {
        const p = this.cellToWorld(at.r, at.c);
        this.fx.ring(p, 0xfde68a, 0.8);
        this.fx.burst(p, 0xfbbf24, 20, 2.5, 0.55);
      }
    }
    if (type === 'swap_invalid') {
      // brief shake handled in update via selection
    }
  }

  update(dt: number, time: number): void {
    this.fx.update(dt);
    const bobAmp = this.reducedMotion ? 0 : 0.03;
    for (const v of this.pieceViews.values()) {
      const selected =
        this.selected && v.r === this.selected.r && v.c === this.selected.c;
      const hover = this.hover && v.r === this.hover.r && v.c === this.hover.c;
      const base = this.cellToWorld(v.r, v.c);
      const bob = Math.sin(time * 2.2 + v.bobPhase) * bobAmp;
      v.mesh.position.x = THREE.MathUtils.lerp(v.mesh.position.x, base.x, 0.35);
      v.mesh.position.y = THREE.MathUtils.lerp(v.mesh.position.y, base.y + bob + (selected ? 0.12 : 0), 0.35);
      v.mesh.position.z = base.z + (selected ? 0.15 : hover ? 0.08 : 0);
      const targetScale = selected ? 1.12 : hover ? 1.06 : 1;
      const s = THREE.MathUtils.lerp(v.mesh.scale.x, targetScale, 0.2);
      v.mesh.scale.setScalar(s);
      v.mesh.rotation.z = selected
        ? Math.sin(time * 6) * 0.08
        : Math.sin(time * 1.5 + v.bobPhase) * 0.04 * (this.reducedMotion ? 0 : 1);
    }
  }

  flashHint(a: Coord, b: Coord): void {
    const va = [...this.pieceViews.values()].find((v) => v.r === a.r && v.c === a.c);
    const vb = [...this.pieceViews.values()].find((v) => v.r === b.r && v.c === b.c);
    for (const v of [va, vb]) {
      if (!v) continue;
      const p = this.cellToWorld(v.r, v.c);
      this.fx.ring(p, 0xffffff, 0.4);
    }
  }

  kindColor(kind: PieceKind): number {
    const map: Record<PieceKind, number> = {
      ember: 0xff6b3d,
      tide: 0x2ec4b6,
      gale: 0x7dd3fc,
      root: 0x5a9a4a,
      solar: 0xfbbf24,
      moon: 0xc4b5fd,
      void: 0x6d28d9,
    };
    return map[kind];
  }
}
